import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import {
  buildInvestigationGraph,
  expandInvestigationNode,
  useSavedInvestigations,
  saveInvestigationGraph,
  type InvestigationGraphNode,
  type InvestigationGraphEdge,
} from '../../hooks/useSecOps';

/* ─── Constants ─── */

const ENTITY_TYPES = ['user', 'ip', 'device', 'process', 'file'] as const;
type EntityType = typeof ENTITY_TYPES[number];

const ENTITY_COLORS: Record<EntityType, string> = {
  user: '#3b82f6',
  device: '#22c55e',
  ip: '#ef4444',
  process: '#f97316',
  file: '#a855f7',
};

const ENTITY_LABELS: Record<EntityType, string> = {
  user: 'User',
  device: 'Device',
  ip: 'IP Address',
  process: 'Process',
  file: 'File',
};

/* ─── Force Layout ─── */

interface LayoutNode {
  id: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
}

function runForceLayout(
  nodes: InvestigationGraphNode[],
  edges: InvestigationGraphEdge[],
  width: number,
  height: number,
): Map<string, { x: number; y: number }> {
  const layoutNodes: LayoutNode[] = nodes.map((n, i) => {
    const angle = (2 * Math.PI * i) / nodes.length;
    const r = Math.min(width, height) * 0.3;
    return {
      id: n.id,
      x: width / 2 + r * Math.cos(angle) + (Math.random() - 0.5) * 40,
      y: height / 2 + r * Math.sin(angle) + (Math.random() - 0.5) * 40,
      vx: 0,
      vy: 0,
      radius: nodeRadius(n.risk_score),
    };
  });

  const nodeMap = new Map<string, LayoutNode>();
  for (const ln of layoutNodes) nodeMap.set(ln.id, ln);

  const edgeIndices = edges
    .map(e => ({ s: nodeMap.get(e.source), t: nodeMap.get(e.target) }))
    .filter(e => e.s && e.t) as Array<{ s: LayoutNode; t: LayoutNode }>;

  // Run simulation
  for (let tick = 0; tick < 80; tick++) {
    const alpha = 1 - tick / 80;
    const decay = 0.6 * alpha;

    // Repulsion between all nodes
    for (let i = 0; i < layoutNodes.length; i++) {
      for (let j = i + 1; j < layoutNodes.length; j++) {
        const a = layoutNodes[i];
        const b = layoutNodes[j];
        let dx = b.x - a.x;
        let dy = b.y - a.y;
        let dist = Math.sqrt(dx * dx + dy * dy) || 1;
        if (dist < 1) dist = 1;
        const force = (800 * decay) / (dist * dist);
        const fx = (dx / dist) * force;
        const fy = (dy / dist) * force;
        a.vx -= fx;
        a.vy -= fy;
        b.vx += fx;
        b.vy += fy;
      }
    }

    // Attraction along edges
    for (const { s, t } of edgeIndices) {
      const dx = t.x - s.x;
      const dy = t.y - s.y;
      const dist = Math.sqrt(dx * dx + dy * dy) || 1;
      const force = (dist - 120) * 0.05 * decay;
      const fx = (dx / dist) * force;
      const fy = (dy / dist) * force;
      s.vx += fx;
      s.vy += fy;
      t.vx -= fx;
      t.vy -= fy;
    }

    // Center gravity
    for (const n of layoutNodes) {
      n.vx += (width / 2 - n.x) * 0.01 * decay;
      n.vy += (height / 2 - n.y) * 0.01 * decay;
    }

    // Apply velocities with damping
    for (const n of layoutNodes) {
      n.vx *= 0.7;
      n.vy *= 0.7;
      n.x += n.vx;
      n.y += n.vy;
      // Keep within bounds
      n.x = Math.max(60, Math.min(width - 60, n.x));
      n.y = Math.max(60, Math.min(height - 60, n.y));
    }
  }

  const positions = new Map<string, { x: number; y: number }>();
  for (const ln of layoutNodes) positions.set(ln.id, { x: ln.x, y: ln.y });
  return positions;
}

function nodeRadius(riskScore?: number): number {
  const score = riskScore ?? 10;
  return Math.max(16, Math.min(32, 12 + score * 0.2));
}

/* ─── Component ─── */

export default function InvestigationWorkbenchPage() {
  const [searchValue, setSearchValue] = useState('');
  const [searchType, setSearchType] = useState<EntityType>('ip');
  const [depth, setDepth] = useState(2);
  const [nodes, setNodes] = useState<InvestigationGraphNode[]>([]);
  const [edges, setEdges] = useState<InvestigationGraphEdge[]>([]);
  const [positions, setPositions] = useState<Map<string, { x: number; y: number }>>(new Map());
  const [selectedNode, setSelectedNode] = useState<InvestigationGraphNode | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [rootEntity, setRootEntity] = useState<{ type: string; value: string } | null>(null);
  const [saveName, setSaveName] = useState('');
  const [saveDescription, setSaveDescription] = useState('');
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showSavedList, setShowSavedList] = useState(false);
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isPanning = useRef(false);
  const panStart = useRef({ x: 0, y: 0 });

  const { data: savedData, refresh: refreshSaved } = useSavedInvestigations();
  const savedInvestigations = savedData?.data ?? [];

  const canvasWidth = 900;
  const canvasHeight = 600;

  const recomputeLayout = useCallback((n: InvestigationGraphNode[], e: InvestigationGraphEdge[]) => {
    if (n.length === 0) {
      setPositions(new Map());
      return;
    }
    const pos = runForceLayout(n, e, canvasWidth, canvasHeight);
    setPositions(pos);
  }, []);

  const handleSearch = useCallback(async () => {
    if (!searchValue.trim()) return;
    setLoading(true);
    setError(null);
    setSelectedNode(null);
    try {
      const result = await buildInvestigationGraph({
        root_entity: { type: searchType, value: searchValue.trim() },
        depth,
      });
      setNodes(result.nodes);
      setEdges(result.edges);
      setRootEntity({ type: searchType, value: searchValue.trim() });
      recomputeLayout(result.nodes, result.edges);
      setZoom(1);
      setPan({ x: 0, y: 0 });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to build graph');
    } finally {
      setLoading(false);
    }
  }, [searchValue, searchType, depth, recomputeLayout]);

  const handleExpand = useCallback(async (node: InvestigationGraphNode) => {
    setLoading(true);
    setError(null);
    try {
      const result = await expandInvestigationNode({
        node_id: node.id,
        node_type: node.type,
        node_value: node.label,
      });

      // Merge new nodes/edges
      const existingNodeIds = new Set(nodes.map(n => n.id));
      const newNodes = result.nodes.filter(n => !existingNodeIds.has(n.id));
      const existingEdgeKeys = new Set(edges.map(e => `${e.source}->${e.target}:${e.label}`));
      const newEdges = result.edges.filter(e => !existingEdgeKeys.has(`${e.source}->${e.target}:${e.label}`));

      const mergedNodes = [...nodes, ...newNodes];
      const mergedEdges = [...edges, ...newEdges];
      setNodes(mergedNodes);
      setEdges(mergedEdges);
      recomputeLayout(mergedNodes, mergedEdges);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to expand node');
    } finally {
      setLoading(false);
    }
  }, [nodes, edges, recomputeLayout]);

  const handleSave = useCallback(async () => {
    if (!saveName.trim() || !rootEntity) return;
    try {
      await saveInvestigationGraph({
        name: saveName.trim(),
        description: saveDescription.trim() || undefined,
        nodes,
        edges,
        root_entity: rootEntity,
      });
      setShowSaveModal(false);
      setSaveName('');
      setSaveDescription('');
      refreshSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save investigation');
    }
  }, [saveName, saveDescription, nodes, edges, rootEntity, refreshSaved]);

  const handleLoadSaved = useCallback(async (inv: { root_entity: { type: string; value: string } }) => {
    setSearchType(inv.root_entity.type as EntityType);
    setSearchValue(inv.root_entity.value);
    setShowSavedList(false);
    // Re-run search with saved root entity
    setLoading(true);
    setError(null);
    setSelectedNode(null);
    try {
      const result = await buildInvestigationGraph({
        root_entity: inv.root_entity,
        depth,
      });
      setNodes(result.nodes);
      setEdges(result.edges);
      setRootEntity(inv.root_entity);
      recomputeLayout(result.nodes, result.edges);
      setZoom(1);
      setPan({ x: 0, y: 0 });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load investigation');
    } finally {
      setLoading(false);
    }
  }, [depth, recomputeLayout]);

  const handleResetLayout = useCallback(() => {
    recomputeLayout(nodes, edges);
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, [nodes, edges, recomputeLayout]);

  // Pan handlers
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return;
    // Only start pan if clicking on the SVG background
    if ((e.target as Element).tagName === 'svg' || (e.target as Element).tagName === 'rect') {
      isPanning.current = true;
      panStart.current = { x: e.clientX - pan.x, y: e.clientY - pan.y };
    }
  }, [pan]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isPanning.current) return;
    setPan({ x: e.clientX - panStart.current.x, y: e.clientY - panStart.current.y });
  }, []);

  const handleMouseUp = useCallback(() => {
    isPanning.current = false;
  }, []);

  // Wheel zoom
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const handler = (e: WheelEvent) => {
      e.preventDefault();
      setZoom(z => Math.max(0.3, Math.min(3, z - e.deltaY * 0.001)));
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, []);

  // Memoize edge lines for rendering
  const edgeLines = useMemo(() => {
    return edges.map((edge, i) => {
      const sPos = positions.get(edge.source);
      const tPos = positions.get(edge.target);
      if (!sPos || !tPos) return null;
      const midX = (sPos.x + tPos.x) / 2;
      const midY = (sPos.y + tPos.y) / 2;
      return { edge, i, sPos, tPos, midX, midY };
    }).filter(Boolean) as Array<{ edge: InvestigationGraphEdge; i: number; sPos: { x: number; y: number }; tPos: { x: number; y: number }; midX: number; midY: number }>;
  }, [edges, positions]);

  const nodeElements = useMemo(() => {
    return nodes.map(node => {
      const pos = positions.get(node.id);
      if (!pos) return null;
      const r = nodeRadius(node.risk_score);
      const color = ENTITY_COLORS[node.type] || '#6b7280';
      const isSelected = selectedNode?.id === node.id;
      return { node, pos, r, color, isSelected };
    }).filter(Boolean) as Array<{ node: InvestigationGraphNode; pos: { x: number; y: number }; r: number; color: string; isSelected: boolean }>;
  }, [nodes, positions, selectedNode]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Investigation Workbench</h1>
          <p className="text-sm text-white/50 mt-1">Visual graph-based entity investigation</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowSavedList(!showSavedList)}
            className="rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm text-white/70 hover:bg-white/10 transition-colors"
          >
            Saved ({savedInvestigations.length})
          </button>
          {nodes.length > 0 && (
            <button
              onClick={() => setShowSaveModal(true)}
              className="rounded-lg bg-indigo-600 hover:bg-indigo-500 px-3 py-2 text-sm text-white transition-colors"
            >
              Save Investigation
            </button>
          )}
        </div>
      </div>

      {/* Search Bar */}
      <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
        <div className="flex items-center gap-3">
          <select
            value={searchType}
            onChange={e => setSearchType(e.target.value as EntityType)}
            className="rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm text-white [&>option]:bg-gray-900"
          >
            {ENTITY_TYPES.map(t => (
              <option key={t} value={t}>{ENTITY_LABELS[t]}</option>
            ))}
          </select>
          <input
            type="text"
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            placeholder="Enter IP, username, hostname, file hash..."
            className="flex-1 rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
          <select
            value={depth}
            onChange={e => setDepth(Number(e.target.value))}
            className="rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm text-white [&>option]:bg-gray-900"
          >
            <option value={1}>Depth 1</option>
            <option value={2}>Depth 2</option>
            <option value={3}>Depth 3</option>
          </select>
          <button
            onClick={handleSearch}
            disabled={loading || !searchValue.trim()}
            className="rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 px-4 py-2 text-sm font-medium text-white transition-colors"
          >
            {loading ? 'Building...' : 'Investigate'}
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg bg-red-500/10 border border-red-500/20 px-4 py-3 text-sm text-red-400">
          {error}
        </div>
      )}

      {/* Saved Investigations Dropdown */}
      {showSavedList && savedInvestigations.length > 0 && (
        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
          <h3 className="text-sm font-medium text-white/70 mb-3">Saved Investigations</h3>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {savedInvestigations.map(inv => (
              <button
                key={inv.id}
                onClick={() => handleLoadSaved(inv)}
                className="w-full text-left rounded-lg bg-white/5 hover:bg-white/10 p-3 transition-colors"
              >
                <div className="text-sm font-medium text-white">{inv.name}</div>
                <div className="text-xs text-white/40 mt-1">
                  {inv.root_entity.type}: {inv.root_entity.value} &middot; {inv.node_count} nodes &middot; {inv.edge_count} edges
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="flex gap-4">
        {/* Graph Canvas */}
        <div
          ref={containerRef}
          className="flex-1 rounded-xl border border-white/10 bg-white/[0.02] relative overflow-hidden"
          style={{ minHeight: 600 }}
        >
          {/* Controls */}
          <div className="absolute top-3 right-3 z-10 flex gap-1">
            <button
              onClick={() => setZoom(z => Math.min(3, z + 0.2))}
              className="rounded-md bg-white/10 hover:bg-white/20 w-8 h-8 flex items-center justify-center text-white/70 text-lg transition-colors"
              title="Zoom in"
            >+</button>
            <button
              onClick={() => setZoom(z => Math.max(0.3, z - 0.2))}
              className="rounded-md bg-white/10 hover:bg-white/20 w-8 h-8 flex items-center justify-center text-white/70 text-lg transition-colors"
              title="Zoom out"
            >-</button>
            <button
              onClick={handleResetLayout}
              className="rounded-md bg-white/10 hover:bg-white/20 px-2 h-8 flex items-center justify-center text-white/70 text-xs transition-colors"
              title="Reset layout"
            >Reset</button>
          </div>

          {/* Legend */}
          <div className="absolute bottom-3 left-3 z-10 flex gap-3">
            {ENTITY_TYPES.map(t => (
              <div key={t} className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: ENTITY_COLORS[t] }} />
                <span className="text-[10px] text-white/50">{ENTITY_LABELS[t]}</span>
              </div>
            ))}
          </div>

          {nodes.length === 0 ? (
            <div className="flex items-center justify-center h-full text-white/30 text-sm">
              Enter an entity above to start investigating
            </div>
          ) : (
            <svg
              ref={svgRef}
              width="100%"
              height="100%"
              viewBox={`0 0 ${canvasWidth} ${canvasHeight}`}
              className="cursor-grab active:cursor-grabbing"
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            >
              <rect x="0" y="0" width={canvasWidth} height={canvasHeight} fill="transparent" />
              <g transform={`translate(${pan.x}, ${pan.y}) scale(${zoom})`}>
                {/* Edges */}
                {edgeLines.map(({ edge, i, sPos, tPos, midX, midY }) => (
                  <g key={`edge-${i}`}>
                    <line
                      x1={sPos.x}
                      y1={sPos.y}
                      x2={tPos.x}
                      y2={tPos.y}
                      stroke="rgba(255,255,255,0.15)"
                      strokeWidth={Math.min(3, 1 + (edge.count ?? 1) * 0.3)}
                    />
                    <text
                      x={midX}
                      y={midY - 6}
                      textAnchor="middle"
                      fill="rgba(255,255,255,0.35)"
                      fontSize="9"
                      fontFamily="system-ui"
                    >
                      {edge.label.replace(/_/g, ' ')}
                      {(edge.count ?? 1) > 1 ? ` (${edge.count})` : ''}
                    </text>
                  </g>
                ))}

                {/* Nodes */}
                {nodeElements.map(({ node, pos, r, color, isSelected }) => (
                  <g
                    key={node.id}
                    onClick={(e) => { e.stopPropagation(); setSelectedNode(node); }}
                    className="cursor-pointer"
                  >
                    {/* Glow for selected */}
                    {isSelected && (
                      <circle cx={pos.x} cy={pos.y} r={r + 6} fill="none" stroke={color} strokeWidth={2} opacity={0.5} />
                    )}
                    {/* Node circle */}
                    <circle
                      cx={pos.x}
                      cy={pos.y}
                      r={r}
                      fill={color}
                      fillOpacity={0.2}
                      stroke={color}
                      strokeWidth={isSelected ? 2.5 : 1.5}
                    />
                    {/* Type icon text */}
                    <text
                      x={pos.x}
                      y={pos.y + 4}
                      textAnchor="middle"
                      fill={color}
                      fontSize="12"
                      fontWeight="bold"
                      fontFamily="system-ui"
                      pointerEvents="none"
                    >
                      {node.type[0].toUpperCase()}
                    </text>
                    {/* Label below */}
                    <text
                      x={pos.x}
                      y={pos.y + r + 14}
                      textAnchor="middle"
                      fill="rgba(255,255,255,0.7)"
                      fontSize="10"
                      fontFamily="system-ui"
                      pointerEvents="none"
                    >
                      {node.label.length > 24 ? node.label.substring(0, 22) + '...' : node.label}
                    </text>
                  </g>
                ))}
              </g>
            </svg>
          )}
        </div>

        {/* Detail Panel */}
        {selectedNode && (
          <div className="w-80 rounded-xl border border-white/10 bg-white/[0.03] p-4 space-y-4 self-start">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-white">Node Detail</h3>
              <button
                onClick={() => setSelectedNode(null)}
                className="text-white/40 hover:text-white/70 text-lg leading-none"
              >&times;</button>
            </div>

            {/* Entity type badge */}
            <div className="flex items-center gap-2">
              <span
                className="inline-block w-3 h-3 rounded-full"
                style={{ backgroundColor: ENTITY_COLORS[selectedNode.type] }}
              />
              <span className="text-sm text-white/70">{ENTITY_LABELS[selectedNode.type]}</span>
            </div>

            {/* Label */}
            <div>
              <div className="text-xs text-white/40 mb-1">Value</div>
              <div className="text-sm text-white font-mono break-all">{selectedNode.label}</div>
            </div>

            {/* Risk score */}
            {selectedNode.risk_score !== undefined && (
              <div>
                <div className="text-xs text-white/40 mb-1">Risk Score</div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 rounded-full bg-white/10 overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${selectedNode.risk_score}%`,
                        backgroundColor: selectedNode.risk_score >= 75 ? '#ef4444' : selectedNode.risk_score >= 50 ? '#f97316' : '#22c55e',
                      }}
                    />
                  </div>
                  <span className="text-sm font-medium text-white">{selectedNode.risk_score}</span>
                </div>
              </div>
            )}

            {/* Properties */}
            <div>
              <div className="text-xs text-white/40 mb-2">Properties</div>
              <div className="space-y-1.5">
                {Object.entries(selectedNode.properties).map(([key, val]) => (
                  <div key={key} className="flex justify-between text-xs">
                    <span className="text-white/50">{key.replace(/_/g, ' ')}</span>
                    <span className="text-white/80 font-mono">{val}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Related alerts count */}
            {selectedNode.properties.alert_count && (
              <div>
                <div className="text-xs text-white/40 mb-1">Related Alerts</div>
                <span className="text-lg font-bold text-white">{selectedNode.properties.alert_count}</span>
              </div>
            )}

            {/* Expand button */}
            <button
              onClick={() => handleExpand(selectedNode)}
              disabled={loading}
              className="w-full rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 px-3 py-2 text-sm font-medium text-white transition-colors"
            >
              {loading ? 'Expanding...' : 'Expand Node'}
            </button>
          </div>
        )}
      </div>

      {/* Stats bar */}
      {nodes.length > 0 && (
        <div className="flex items-center gap-6 text-xs text-white/40">
          <span>{nodes.length} nodes</span>
          <span>{edges.length} edges</span>
          {rootEntity && (
            <span>Root: {rootEntity.type}:{rootEntity.value}</span>
          )}
        </div>
      )}

      {/* Save Modal */}
      {showSaveModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#0f0f0f] p-6 space-y-4">
            <h3 className="text-lg font-semibold text-white">Save Investigation</h3>
            <div>
              <label className="block text-xs text-white/50 mb-1">Name</label>
              <input
                type="text"
                value={saveName}
                onChange={e => setSaveName(e.target.value)}
                className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                placeholder="e.g., Suspicious IP Investigation"
              />
            </div>
            <div>
              <label className="block text-xs text-white/50 mb-1">Description (optional)</label>
              <textarea
                value={saveDescription}
                onChange={e => setSaveDescription(e.target.value)}
                rows={3}
                className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:ring-1 focus:ring-indigo-500 resize-none"
                placeholder="Brief description of the investigation..."
              />
            </div>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setShowSaveModal(false)}
                className="rounded-lg bg-white/5 hover:bg-white/10 px-4 py-2 text-sm text-white/70 transition-colors"
              >Cancel</button>
              <button
                onClick={handleSave}
                disabled={!saveName.trim()}
                className="rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 px-4 py-2 text-sm font-medium text-white transition-colors"
              >Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
